import { isValidInsightId } from './insight-id.utils';

interface MutableFlag {
  current: boolean;
}

// Guard the one-shot effect against concurrent re-fires by setting the flag up
// front, but clear it if the async call rejects so a later effect pass can retry
// instead of the transient failure being permanent.
function fireGuarded(
  guardRef: MutableFlag,
  run: () => void | Promise<unknown>
): void {
  guardRef.current = true;
  void Promise.resolve(run()).catch(() => {
    guardRef.current = false;
  });
}

export function startMobileListeningOnRecording({
  isMobileMode,
  isRecording,
  startedRef,
  startListening,
}: {
  isMobileMode: boolean;
  isRecording: boolean;
  startedRef: MutableFlag;
  startListening: (options?: { skipLocalMic?: boolean }) => Promise<void>;
}): void {
  if (isMobileMode && isRecording && !startedRef.current) {
    startedRef.current = true;
    startListening({ skipLocalMic: true }).catch(() => undefined);
  }
}

export function linkMobileAudioForInsight({
  isMobileMode,
  insightId,
  mobileUniqueId,
  linkedRef,
  linkMobileAudio,
}: {
  isMobileMode: boolean;
  insightId: string | null;
  mobileUniqueId: string | null;
  linkedRef: MutableFlag;
  linkMobileAudio: (
    insightId: string,
    uniqueId: string
  ) => void | Promise<unknown>;
}): void {
  if (
    isMobileMode &&
    insightId &&
    isValidInsightId(insightId) &&
    mobileUniqueId &&
    !linkedRef.current
  ) {
    fireGuarded(linkedRef, () => linkMobileAudio(insightId, mobileUniqueId));
  }
}

export function syncMobileRecordingTimestamp({
  isMobileMode,
  isRecording,
  mobileUniqueId,
  syncedRef,
  syncTimestamp,
}: {
  isMobileMode: boolean;
  isRecording: boolean;
  mobileUniqueId: string | null;
  syncedRef: MutableFlag;
  syncTimestamp: (uniqueId: string) => void | Promise<unknown>;
}): void {
  if (isMobileMode && isRecording && mobileUniqueId && !syncedRef.current) {
    fireGuarded(syncedRef, () => syncTimestamp(mobileUniqueId));
  }
}

export function endMobileSessionWhenOver({
  isMobileMode,
  isSessionOver,
  isFinalizingRecording,
  mobileUniqueId,
  hasFinalizedRef,
  sessionEndedRef,
  endMobileSession,
}: {
  isMobileMode: boolean;
  isSessionOver: boolean;
  isFinalizingRecording: boolean;
  mobileUniqueId: string | null;
  hasFinalizedRef: MutableFlag;
  sessionEndedRef: MutableFlag;
  endMobileSession: (uniqueId: string) => void | Promise<unknown>;
}): void {
  if (isFinalizingRecording) {
    hasFinalizedRef.current = true;
  }

  if (
    isMobileMode &&
    isSessionOver &&
    hasFinalizedRef.current &&
    !isFinalizingRecording &&
    mobileUniqueId &&
    !sessionEndedRef.current
  ) {
    fireGuarded(sessionEndedRef, () => endMobileSession(mobileUniqueId));
  }
}
